import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminBlogAuthorAccessList } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const PAGE_SIZE = 30;
const sortOptions = [
  { value: 'updated-desc', label: '更新日が新しい順' },
];

const tableStyle = { minWidth: '72rem' };
const indexColumnStyle = { width: '4rem', minWidth: '4rem' };
const authorColumnStyle = { minWidth: '18rem' };
const emailColumnStyle = { minWidth: '18rem' };
const statusColumnStyle = { width: '9rem', minWidth: '9rem' };
const dateColumnStyle = { width: '11rem', minWidth: '11rem' };
const actionColumnStyle = { width: '8rem', minWidth: '8rem' };

const statusToUi = (isActive) => ({
  label: isActive ? '有効' : '停止中',
  tone: isActive ? 'published' : 'draft',
});

const AdminBlogAuthorsListPage = () => {
  const content = yowanPageContent['/admin/content/blog-authors'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [sortKey, setSortKey] = useState('updated-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadRows = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const payload = await fetchYowanAdminBlogAuthorAccessList({
          page: currentPage,
          pageSize: PAGE_SIZE,
          q: searchQuery.trim(),
          isActive: isActiveFilter,
        });

        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError(content?.loadErrorMessage || 'ブログ執筆者を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadRows();

    return () => {
      isMounted = false;
    };
  }, [content?.loadErrorMessage, currentPage, isActiveFilter, searchQuery]);

  if (!content) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(apiCount / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * PAGE_SIZE;

  return (
    <AdminBlogShell activeNavHref="/admin/content/blog-authors" routeKey="/admin/content/blog-authors">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanPreviewQueryIfNeeded(content.primaryAction.href, preservedDomainSearch)}
          >
            {content.primaryAction.label}
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => setSortKey(event.target.value)}
        metaText={`${apiCount}件中 ${rows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder={content.searchPlaceholder}
        sortOptions={sortOptions}
      >
        <div className="yowan-admin-toolbar__group">
          <label className="c-form__field yowan-admin-toolbar__field">
            <span className="c-form__label">状態</span>
            <select
              className="c-form__control yowan-admin-toolbar__sort"
              value={isActiveFilter}
              onChange={(event) => {
                setIsActiveFilter(event.target.value);
                setCurrentPage(1);
              }}
            >
              {content.filters.isActive.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </AdminListToolbar>

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table" style={tableStyle}>
          <thead>
            <tr>
              <th scope="col" style={indexColumnStyle}>#</th>
              <th scope="col" style={authorColumnStyle}>著者名</th>
              <th scope="col" style={emailColumnStyle}>ログインメール</th>
              <th scope="col" style={statusColumnStyle}>投稿公開</th>
              <th scope="col" style={statusColumnStyle}>状態</th>
              <th scope="col" style={dateColumnStyle}>最終招待</th>
              <th scope="col" style={dateColumnStyle}>受諾</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const statusUi = statusToUi(row.isActive);

              return (
                <tr key={row.id}>
                  <td style={indexColumnStyle}>{startIndex + index + 1}</td>
                  <td style={authorColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.authorName || '-'}</p>
                    {row.profileUrl ? <p className="yowan-admin-resource-table__subline">{row.profileUrl}</p> : null}
                  </td>
                  <td style={emailColumnStyle}>{row.email || '-'}</td>
                  <td style={statusColumnStyle}>{row.canPublishBlog ? '可能' : '下書きのみ'}</td>
                  <td style={statusColumnStyle}>
                    <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}>
                      {statusUi.label}
                    </span>
                  </td>
                  <td style={dateColumnStyle}>{row.lastInvitedAt || row.invitedAt || '-'}</td>
                  <td style={dateColumnStyle}>{row.acceptedAt || '-'}</td>
                  <td style={actionColumnStyle}>
                    <div className="yowan-admin-resource-table__actions">
                      <Link to={appendYowanPreviewQueryIfNeeded(row.editHref, preservedDomainSearch)}>編集</Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={8}>{loadError || '表示できるブログ執筆者がありません。'}</td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={8}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminBlogAuthorsListPage;
